import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { SyncLoader } from 'react-spinners';
import useAuthenticate from '../authentication/authenticate';

import axios from 'axios';
import config from '../../config';

import '../../styles/popup.css';
import '../../styles/tables.css'
import '../../styles/buttons.css';
import '../../styles/status.css';
import './styles/detailedPropertyPanel.css';

import CreateParcelForm from '../forms/createParcelForm';
import EditParcelForm from '../forms/editParcelForm';
import CreateVisitorLogForm from '../forms/visitorLogForm';
import AddEditRoleForm from '../forms/addEditRoleForm';

import CreateRequestForm from '../forms/createRequestForm';
import EditRequestForm from '../forms/editRequestForm';
import CreateWorkPermitForm from '../forms/createWorkPermitForm';
import EditWorkPermitForm from '../forms/editWorkPermitForm';
import CreateGatePassForm from '../forms/createGatePassForm';

import GroupedRoles from "../general/groupedRoles";
import DeleteConfirmation from "../general/deleteConfirmation";

const PropertyDetailsPanel = ({ property, onClose }) => {

    useEffect(() => {
        console.log('Property data:', property);
    }, [property]);

    return (
        <div>
            <div className="popup-overlay" onClick={onClose}></div>
            <div className="property-details-panel">
                <h2>Unit Details</h2>
                <div className="property-details">
                    <hr />
                    <DetailsSection property={property} />
                    <ExpandableSection title="Requests">
                        <RequestsSection unitId={property._id} property={property} />
                    </ExpandableSection>
                    <ExpandableSection title="Parcel History">
                        <ParcelSection unitId={property._id} property={property} />
                    </ExpandableSection>
                    <ExpandableSection title="Visitor Log">
                        <VisitorLog unitId={property._id} property={property} />
                    </ExpandableSection>
                    <ExpandableSection title="Roles">
                        <RolesSection property={property} />
                    </ExpandableSection>
                </div>
            </div>
        </div>
    );
};

//=======================
//  SECTION COMPONENTS ||
//=======================

// Expandable Section Component
const ExpandableSection = ({ title, children }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="expandable-section">
            <div className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
                <h3>{title}</h3>
                <span className="toggle-icon">{isExpanded ? <i className="fas fa-caret-down"></i> : <i className="fas fa-caret-up"></i>}</span>
            </div>
            {isExpanded && <div className="section-content">{children}</div>}
        </div>
    );
};

// Individual Sections
const DetailsSection = ({ property }) => {
    const [images, setImages] = useState([]);

    useEffect(() => {
        if (property.images && property.images.length > 0) {
            setImages(property.images);
        }
    }, [property]);

    const renderValue = (value, suffix = "") => {
        const isEmpty =
            value === null ||
            value === undefined ||
            value === "" ||
            (typeof value === "string" && value.trim() === "");

        return isEmpty ? (
            <span style={{ fontStyle: "italic", color: "#888" }}>Not available</span>
        ) : (
            `${value}${suffix}`
        );
    };

    return (
        <div>
            <div className="detail-column">
                <p className="detail-label">Property Address</p>
                <p className="detail">{`${property.property.name}, ${property.unitNumber ? `${property.property.address.streetNumber}, ` : ''}${property.property.address.street}, ${property.property.address.city}, ${property.property.address.state}, ${property.property.address.country} - ${property.property.address.postalCode}`}</p>
            </div>

            <div className="detail-row">
                <div className="detail-column">
                    <p className="detail-label">Status</p>
                    <p className={`detail ${property.isAvailable ? 'available' : 'not-available'}`}>
                        <span className={`property-status-badge ${property.isAvailable ? 'available' : 'not-available'}`}>
                            {property.isAvailable ? 'Available' : 'Not Available'}
                        </span>
                    </p>
                </div>
                <div className="detail-column">
                    <p className="detail-label">Market Rent</p>
                    <p className="detail">
                        {property.price ? (
                            `PHP ${property.price} /week`
                        ) : (
                            <span style={{ fontStyle: "italic", color: "#888" }}>Not available</span>
                        )}
                    </p>
                </div>
                <div className="detail-column">
                    <p className="detail-label">Floor Area</p>
                    <p className="detail">
                        {renderValue(property.floorArea, " m²")}
                    </p>
                </div>
            </div>

            <div className="detail-row">
                <div className="detail-column">
                    <p className="detail-label">Bedrooms</p>
                    <p className="detail">{renderValue(property.bedrooms)}</p>
                </div>
                <div className="detail-column">
                    <p className="detail-label">Bathrooms</p>
                    <p className="detail">{renderValue(property.bathrooms)}</p>
                </div>
                <div className="detail-column">
                    <p className="detail-label">Lot Area</p>
                    <p className="detail">
                        {renderValue(property.lotArea, " m²")}
                    </p>
                </div>
            </div>

            <div className="detail-row">
                <div className="detail-column full-width">
                    <p className="detail-label">Unit Description</p>
                    <p className="detail">
                        {property.description.trim() ? (
                            <p className="detail">{property.description}</p>
                        ) : (
                            <span style={{ fontStyle: "italic", color: "#888" }}>Not available</span>
                        )}
                    </p>
                </div>
            </div>

            <div className="detail-row">
                <div className="detail-column">
                    <p className="detail-label">Property Amenities</p>
                    {property.property.amenities && property.property.amenities.length > 0 ? (
                        <p className="detail">{property.property.amenities.join(', ')}</p>
                    ) : (
                        <p className="detail" style={{ fontStyle: "italic", color: "#888" }}>Not available</p>
                    )}
                </div>
            </div>

            <div className="detail-row">
                <div className="detail-column">
                    <p className="detail-label">Unit Photos</p>
                    <div className="photo-gallery">
                        {images.length > 0 ? (
                            images.map((photo) => (
                                <img
                                    key={photo._id}
                                    src={photo.key}
                                    alt={photo.altText || "Unit Photo"}
                                    className="unit-photo"
                                />
                            ))
                        ) : (
                            <p className="detail" style={{ fontStyle: 'italic', color: '#888' }}>No photos available</p>
                        )}
                    </div>
                </div>
            </div>

            <hr />
        </div>
    );
};


//====================
// REQUESTS SECTION ||
//====================

const RequestsSection = ({ unitId, property }) => {
  const [requests, setRequests] = useState([]);
  const [gatePasses, setGatePasses] = useState([]);
  const [workPermits, setWorkPermits] = useState([]);

  const { user } = useAuthenticate();
  const account = user?.active?.manager || null;

  const [editingRequest, setEditingRequest] = useState(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null);

  const [requestToDelete, setRequestToDelete] = useState(null);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);

  const [selectedTab, setSelectedTab] = useState("ServiceRequests");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data when tab changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let response;

        if (selectedTab === "ServiceRequests") {
          response = await axios.get(`${config.API_URL}/v2/request?unit=${unitId}&limit=5`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
          });
          setRequests(
            response.data.requests.filter(
              (r) => !["AmenityBooking", "GatePass", "WorkPermit"].includes(r.type)
            )
          );
        }

        if (selectedTab === "GatePasses") {
          response = await axios.get(
            `${config.API_URL}/v2/request?unit=${unitId}&limit=5&type=GatePass`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
            }
          );
          setGatePasses(response.data.requests);
        }

        if (selectedTab === "WorkPermits") {
          response = await axios.get(
            `${config.API_URL}/v2/request?unit=${unitId}&limit=5&type=WorkPermit`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
            }
          );
          setWorkPermits(response.data.requests);
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [unitId, selectedTab]);

  // Handlers
  const handleEditRequest = (item) => {
    setEditingRequest(item);
    setOverlayMode("edit");
    setIsOverlayOpen(true);
  };

  const handleCreate = (type) => {
    setEditingRequest(null);
    setOverlayMode(`create_${type}`);
    setIsOverlayOpen(true);
  };

  const handleDeleteRequest = (item) => {
    setRequestToDelete(item);
    setDeleteOverlayOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${config.API_URL}/v2/request/${requestToDelete._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });

      if (selectedTab === "ServiceRequests") {
        setRequests((prev) => prev.filter((r) => r._id !== requestToDelete._id));
      } else if (selectedTab === "WorkPermits") {
        setWorkPermits((prev) => prev.filter((r) => r._id !== requestToDelete._id));
      } else if (selectedTab === "GatePasses") {
        setGatePasses((prev) => prev.filter((r) => r._id !== requestToDelete._id));
      }

      toast.success("Request deleted successfully");
      setDeleteOverlayOpen(false);
      setRequestToDelete(null);
    } catch (err) {
      console.error("Failed to delete request:", err);
      toast.error("Failed to delete request. Please try again.");
    }
  };

  if (loading)
    return (
      <div style={{ display: "flex", width: "100%", justifyContent: "center" }}>
        <SyncLoader color="#a8a8a8" size={8} />
      </div>
    );

  if (error) return <p>Error: {error}</p>;

  const renderTable = () => {
    switch (selectedTab) {
      case "ServiceRequests":
        return (
          <table className="page-table mb-[30px]">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Requested On</th>
                <th>Priority</th>
                <th>Status</th>
                <th className="actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {requests.length > 0 ? (
                requests.map((req) => (
                  <tr key={req._id}>
                    <td>{req.details?.title || "Untitled"}</td>
                    <td>{req.type}</td>
                    <td>{new Date(req.createdAt).toLocaleDateString()}</td>
                    <td>{req.details?.priority || "N/A"}</td>
                    <td>{req.status}</td>
                    <td>
                      <button className="action-buttons" onClick={() => handleEditRequest(req)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="action-buttons" onClick={() => handleDeleteRequest(req)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Service Requests
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        );

      case "WorkPermits":
        return (
          <table className="page-table mb-[30px]">
            <thead>
              <tr>
                <th>Service Provider</th>
                <th>Work Category</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th className="actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {workPermits.length > 0 ? (
                workPermits.map((wp) => (
                  <tr key={wp._id}>
                    <td>{wp.details?.serviceProvider || "N/A"}</td>
                    <td>{wp.details?.workCategory || "N/A"}</td>
                    <td>
                      {wp.details?.startDate
                        ? new Date(wp.details.startDate).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td>
                      {wp.details?.endDate
                        ? new Date(wp.details.endDate).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td>{wp.status}</td>
                    <td>
                      <button className="action-buttons" onClick={() => handleEditRequest(wp)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="action-buttons" onClick={() => handleDeleteRequest(wp)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Work Permits
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        );

      case "GatePasses":
        return (
          <table className="page-table mb-[30px]">
            <thead>
              <tr>
                <th>Title</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Priority</th>
                <th>Status</th>
                <th className="actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {gatePasses.length > 0 ? (
                gatePasses.map((gp) => (
                  <tr key={gp._id}>
                    <td>{gp.details?.title || "N/A"}</td>
                    <td>
                      {gp.details?.startDate
                        ? new Date(gp.details.startDate).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td>
                      {gp.details?.endDate
                        ? new Date(gp.details.endDate).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td>{gp.details?.priority || "N/A"}</td>
                    <td>{gp.status}</td>
                    <td>
                      <button className="action-buttons" onClick={() => handleEditRequest(gp)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="action-buttons" onClick={() => handleDeleteRequest(gp)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Gate Passes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {/* Tabs + Create Button */}
      <div className="flex justify-between items-center mt-3 mb-3">
        <div className="flex flex-row gap-[10px]">
          <button
            className={`tab-buttons ${selectedTab === "ServiceRequests" ? "active" : ""}`}
            onClick={() => setSelectedTab("ServiceRequests")}
          >
            Service Requests
          </button>
          <button
            className={`tab-buttons ${selectedTab === "GatePasses" ? "active" : ""}`}
            onClick={() => setSelectedTab("GatePasses")}
          >
            Gate Passes
          </button>
          <button
            className={`tab-buttons ${selectedTab === "WorkPermits" ? "active" : ""}`}
            onClick={() => setSelectedTab("WorkPermits")}
          >
            Work Permits
          </button>
        </div>

        {/* Create Button beside tabs */}
        <div>
          {selectedTab === "ServiceRequests" && (
            <button className="property-panel-button" onClick={() => handleCreate("serviceRequest")}>
              Create Request
            </button>
          )}
          {selectedTab === "GatePasses" && (
            <button className="property-panel-button" onClick={() => handleCreate("gatePass")}>
              Create Gate Pass
            </button>
          )}
          {selectedTab === "WorkPermits" && (
            <button className="property-panel-button" onClick={() => handleCreate("workPermit")}>
              Create Work Permit
            </button>
          )}
        </div>
      </div>

      {renderTable()}

      {/* Delete Confirmation */}
      {deleteOverlayOpen && (
        <DeleteConfirmation setIsOverlayOpen={setDeleteOverlayOpen} onConfirm={confirmDelete} />
      )}

      {/* Overlay Section */}
      {isOverlayOpen && (
        <>
          {/* Edit Request */}
          {overlayMode === "edit" && selectedTab === "ServiceRequests" && (
            <EditRequestForm
              requestId={editingRequest._id}
              property={{ selectedProperty: { value: property?._id || property?.value } }}
              onClose={() => {
                setIsOverlayOpen(false);
                setOverlayMode(null);
              }}
              onRequestUpdated={(updatedRequest) => {
                setRequests((prev) =>
                  prev.map((r) => (r._id === updatedRequest._id ? updatedRequest : r))
                );
                setIsOverlayOpen(false);
                setOverlayMode(null);
              }}
            />
          )}

          {/* Edit Work Permit */}
            {overlayMode === "edit" && selectedTab === "WorkPermits" && (
            <EditWorkPermitForm
                row={editingRequest}
                property={property}
                units={[]}
                propertyAccounts={[]}
                onClose={() => {
                setIsOverlayOpen(false);
                setOverlayMode(null);
                }}
            />
            )}

        {/* Edit Gate Pass */}
            {overlayMode === "edit" && selectedTab === "GatePasses" && (
            <CreateGatePassForm
                onClose={() => {
                setIsOverlayOpen(false);
                setOverlayMode(null);
                }}
                account={account}
                property={property}
                unitOptions={[{ _id: property._id, label: property.unitNumber || "N/A" }]}
                role="manager"
                selectedRequest={editingRequest}
                onRequestUpdated={(updatedGatePass) => {
                setGatePasses((prev) =>
                    prev.map((g) => (g._id === updatedGatePass._id ? updatedGatePass : g))
                );
                setIsOverlayOpen(false);
                setOverlayMode(null);
                }}
            />
            )}

          {/* Create Request */}
          {overlayMode === "create_serviceRequest" && (
            <CreateRequestForm
              property={property}
              onClose={() => {
                setIsOverlayOpen(false);
                setOverlayMode(null);
              }}
              onRequestCreated={(newRequest) => {
                setRequests((prev) => [newRequest, ...prev]);
                setIsOverlayOpen(false);
                setOverlayMode(null);
              }}
            />
          )}

          {/* Create Work Permit */}
          {overlayMode === "create_workPermit" && (
            <CreateWorkPermitForm
              property={property}
              onClose={() => {
                setIsOverlayOpen(false);
                setOverlayMode(null);
              }}
              onRequestCreated={(newWorkPermit) => {
                setWorkPermits((prev) => [newWorkPermit, ...prev]);
                setIsOverlayOpen(false);
                setOverlayMode(null);
              }}
            />
          )}

          {/* Create Gate Pass */}
          {overlayMode === "create_gatePass" && (
            <CreateGatePassForm
              onClose={() => {
                setIsOverlayOpen(false);
                setOverlayMode(null);
              }}
              account={account}
              property={property}
              unitOptions={[{ _id: property._id, label: property.unitNumber || "N/A" }]}
              role="manager"
              onRequestCreated={(newGatePass) => {
                setGatePasses((prev) => [newGatePass, ...prev]);
                setIsOverlayOpen(false);
                setOverlayMode(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

//====================
//  PARCEL SECTION  ||
//====================

const ParcelSection = ({ unitId, property }) => {
    const [parcels, setParcels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formOverlayOpen, setFormOverlayOpen] = useState(false);
    const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);

    const [editingParcel, setEditingParcel] = useState(null);
    const [parcelToDelete, setParcelToDelete] = useState(null);

    useEffect(() => {
        const fetchParcels = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/v2/parcel?unit=${unitId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
                });
                console.log('Fetched parcels:', response.data);
                setParcels(response.data.results);
            } catch (error) {
                setError(error.response ? error.response.data.error || error.response.statusText : error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchParcels();
    }, [unitId]);

    const handleParcelCreated = (newParcel) => {
        setParcels((prevParcels) => [newParcel, ...prevParcels]);
    };

    const handleParcelUpdated = (updatedParcel) => {
        setParcels((prevParcels) =>
            prevParcels.map((parcel) =>
                parcel._id === updatedParcel._id ? updatedParcel : parcel
            )
        );
        setEditingParcel(null);
    };

    const handleDeleteParcel = async () => {
        if (!parcelToDelete) return;

        try {
            await axios.delete(`${config.API_URL}/v2/parcel/${parcelToDelete}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            });
            setDeleteOverlayOpen(false);
            setParcelToDelete(null);
            setParcels((prevParcels) =>
                prevParcels.filter((parcel) => parcel._id !== parcelToDelete)
            );
            toast.success('Parcel deleted successfully');
        } catch (err) {
            console.error('Failed to delete parcel:', err);
            setError('Failed to delete parcel. Please try again.');
            toast.error('Failed to delete parcel. Please try again.');
        }
    };

    const handleCloseForm = () => {
        setFormOverlayOpen(false);
    };

    const handleOpenForm = () => {
        setFormOverlayOpen(true);
    };

    if (loading)
        return (
            <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                <SyncLoader color="#a8a8a8" size={8} />
            </div>
        );

    if (error) return <p>Error: {error}</p>;

    return (
        <div>
            <button onClick={handleOpenForm} className="property-panel-button">
                <i className="fas fa-add"></i>
                Parcel
            </button>
            <table className="page-table">
                <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '23%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '23%' }} />
                    <col style={{ width: '10%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ textAlign: 'center' }}>Carrier</th>
                        <th style={{ textAlign: 'center' }}>Tracking Number</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                        <th style={{ textAlign: 'center' }}>Delivery Date</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {parcels.length > 0 ? (
                        parcels.map((parcel) => (
                            <tr key={parcel._id}>
                                <td style={{ textAlign: 'center' }}>{parcel.carrier}</td>
                                <td style={{ textAlign: 'center' }}>{parcel.trackingNumber}</td>
                                <td style={{ textAlign: 'center' }}>{parcel.status}</td>
                                <td style={{ textAlign: 'center' }}>{new Date(parcel.deliveryDate).toLocaleDateString()}</td>
                                <td>
                                    <button
                                        className="action-buttons"
                                        onClick={() => setEditingParcel(parcel._id)}
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                        className="action-buttons"
                                        onClick={() => {
                                            setParcelToDelete(parcel._id);
                                            setDeleteOverlayOpen(true);
                                        }}
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" style={{ textAlign: 'center' }}>No data found</td>
                        </tr>
                    )}
                </tbody>
            </table>
            {formOverlayOpen && (
                <CreateParcelForm
                    unitId={unitId}
                    property={property}
                    onParcelCreated={handleParcelCreated}
                    onClose={handleCloseForm}
                />
            )}
            {editingParcel && (
                <EditParcelForm
                    parcelId={editingParcel}
                    property={property}
                    onClose={() => setEditingParcel(null)}
                    onParcelUpdated={handleParcelUpdated}
                />
            )}
            {deleteOverlayOpen && (
                <DeleteConfirmation
                    setIsOverlayOpen={setDeleteOverlayOpen}
                    onConfirm={handleDeleteParcel}
                />
            )}
        </div>
    );
};

//====================
//  VISITOR SECTION ||
//====================

const VisitorLog = ({ unitId, property }) => {
    const [visitorLogs, setVisitorLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formOverlayOpen, setFormOverlayOpen] = useState(false);
    const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState(null);

    const [editLog, setEditLog] = useState(null);
    const [logToDelete, setLogToDelete] = useState(null);

    useEffect(() => {
        const fetchVisitorLogs = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/v2/visitor/unit/${unitId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
                });
                setVisitorLogs(response.data);
            } catch (error) {
                setError(error.response ? error.response.data.error || error.response.statusText : error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchVisitorLogs();
    }, [unitId]);

    const handleCreateLogClick = () => {
        setSelectedUnit({
            _id: property._id,
            label: `${property?.unitNumber || 'N/A'}`,
        });
        setFormOverlayOpen(true);
    };

    const handleVisitorLogCreated = (newLog) => {
        setVisitorLogs((prevLogs) => [newLog, ...prevLogs]);
        setFormOverlayOpen(false);
    };

    const handleCheckOut = async (logId) => {
        try {
            const response = await axios.patch(
                `${config.API_URL}/v2/visitor/${logId}/checkout`,
                { checkOut: new Date().toISOString() },
                { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
            );
            setVisitorLogs((prevLogs) =>
                prevLogs.map((log) => (log._id === logId ? { ...log, checkOut: response.data.checkOut } : log))
            );
            toast.success('Visitor checked out successfully!');
        } catch (error) {
            alert("Failed to check out the visitor.");
            toast.error('Failed to check out visitor.');
        }
    };

    const handleEditLog = (log) => {
        setEditLog(log); // Set the log to be edited
        setSelectedUnit({
            _id: log.unit._id,
            label: `${log.unit?.unitNumber || 'N/A'}`,
        });
        setFormOverlayOpen(true); // Open the modal in edit mode
    };

    const handleDeleteLog = async () => {
        if (!logToDelete) return;

        try {
            await axios.delete(`${config.API_URL}/v2/visitor/${logToDelete}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            });
            setDeleteOverlayOpen(false);
            setLogToDelete(null);
            setVisitorLogs((prevLogs) =>
                prevLogs.filter((log) => log._id !== logToDelete)
            );
            toast.success('Visitor log deleted successfully!');
        } catch (error) {
            toast.error("Failed to delete the visitor log.");
        }
    };

    const handleVisitorLogUpdated = async (updatedLog) => {
        try {
            const response = await axios.put(
                `${config.API_URL}/v2/visitor/${updatedLog._id}`,
                updatedLog,
                { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
            );
            setVisitorLogs((prevLogs) =>
                prevLogs.map((log) => (log._id === updatedLog._id ? response.data : log))
            );
            setFormOverlayOpen(false); // Close the modal after successful update
        } catch (error) {
            toast.error("Failed to update the visitor log.");
        }
    };

    if (loading)
        return (
            <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                <SyncLoader color="#a8a8a8" size={8} />
            </div>
        );

    if (error) return <p>Error: {error}</p>;

    return (
        <div>
            <button onClick={handleCreateLogClick} className="property-panel-button">
                <i className="fas fa-add"></i>
                Visitor
            </button>
            <table className="page-table">
                <colgroup>
                    <col style={{ width: '19%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '10%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ textAlign: 'center' }}>Name</th>
                        <th style={{ textAlign: 'center' }}>Mobile</th>
                        <th style={{ textAlign: 'center' }}>Purpose</th>
                        <th style={{ textAlign: 'center' }}>Check-In</th>
                        <th style={{ textAlign: 'center' }}>Check-Out</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {visitorLogs.length > 0 ? (
                        visitorLogs.map((log) => (
                            <tr key={log._id} onClick={() => alert(JSON.stringify(log, null, 2))}>
                                <td style={{ textAlign: 'center' }}>{log.givenName} {log.lastName}</td>
                                <td style={{ textAlign: 'center' }}>{log.mobile || 'N/A'}</td>
                                <td style={{ textAlign: 'center' }}>{log.purpose || 'N/A'}</td>
                                <td style={{ textAlign: 'center' }}>{new Date(log.checkIn).toLocaleDateString()}</td>
                                <td style={{ textAlign: 'center' }}>{log.checkOut ? new Date(log.checkOut).toLocaleDateString() : 'N/A'}</td>
                                <td>
                                    <button className="action-buttons" onClick={(e) => { e.stopPropagation(); handleEditLog(log); }}><i className="fas fa-edit"></i></button>
                                    <button className="action-buttons" onClick={(e) => { e.stopPropagation(); setLogToDelete(log._id); setDeleteOverlayOpen(true) }}><i className="fas fa-trash"></i></button>
                                    <button
                                        className={`action-buttons ${log.checkOut ? "disabled" : ""}`}
                                        onClick={(e) => { e.stopPropagation(); handleCheckOut(log._id); }}
                                        disabled={!!log.checkOut} // Disables button when log.checkOut exists
                                    >
                                        <i className="fas fa-clipboard-check"></i>
                                    </button>
                                </td>
                            </tr>))
                    ) : (
                        <tr>
                            <td colSpan="7" style={{ textAlign: 'center' }}>No data found</td>
                        </tr>
                    )}
                </tbody>
            </table>
            {formOverlayOpen && selectedUnit && (
                <CreateVisitorLogForm
                    unitId={selectedUnit._id}
                    units={[{ value: selectedUnit._id, label: selectedUnit.label }]}
                    onVisitorLogCreated={handleVisitorLogCreated}
                    onVisitorLogUpdated={handleVisitorLogUpdated}
                    onClose={() => {
                        setFormOverlayOpen(false);
                        setEditLog(null);
                        setSelectedUnit(null);
                    }}
                    selectedLog={editLog}
                />
            )}
            {deleteOverlayOpen && (
                <DeleteConfirmation
                    setIsOverlayOpen={setDeleteOverlayOpen}
                    onConfirm={handleDeleteLog}
                />
            )}
        </div>
    );
};

//====================
//   ROLES SECTION  ||
//====================

const RolesSection = ({ property }) => {
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);

    const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/v2/role/unitRole/unit/${property._id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
                });
                setRoles(response.data);
            } catch (err) {
                setError("Failed to fetch roles.");
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRoles();
    }, [property._id]);

    const handleAddRole = () => {
        setIsFormOpen(true);
        setIsEdit(false);
        setSelectedRole(null);
    };

    const handleEditRole = (roleId) => {
        const roleToEdit = roles.find((role) => role._id === roleId);
        setIsFormOpen(true);
        setIsEdit(true);
        setSelectedRole(roleToEdit);
    };

    const handleDeleteRole = async (roleId) => {
        setRoleToDelete(roleId);
        setDeleteOverlayOpen(true);
    };

    const confirmDeleteRole = async () => {
        if (!roleToDelete) return;
        try {
            await axios.delete(`${config.API_URL}/v2/role/unitRole/${roleToDelete}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            });
            setDeleteOverlayOpen(false);
            setRoleToDelete(null);
            setRoles((prevRoles) => prevRoles.filter((role) => role._id !== roleToDelete));
            toast.success("Role deleted successfully!");
        } catch (err) {
            console.error("Failed to delete role:", err);
            setError("Failed to delete role. Please try again.");
            toast.error("Failed to delete role. Please try again.");
        }
    };

    const handleFormSubmit = (newRole) => {
        if (isEdit) {
            setRoles((prevRoles) =>
                prevRoles.map((role) =>
                    role._id === newRole._id ? { ...role, ...newRole } : role
                )
            );
        } else {
            setRoles((prevRoles) => [...prevRoles, newRole]);
        }
        setIsFormOpen(false);
    };

    return (
        <div className="roles-manager">
            <div className="roles-header">
                <button onClick={handleAddRole} className="property-panel-button">
                    <i className="fas fa-add"></i>
                    Role
                </button>
            </div>
            <GroupedRoles
                roles={roles}
                isLoading={isLoading}
                error={error}
                onEditRole={handleEditRole}
                onDeleteRole={handleDeleteRole}
            />
            {isFormOpen && (
                <AddEditRoleForm
                    property={property}
                    role={selectedRole}
                    isEdit={isEdit}
                    onClose={() => setIsFormOpen(false)}
                    onSubmit={handleFormSubmit}
                />
            )}
            {deleteOverlayOpen && (
                <DeleteConfirmation
                    setIsOverlayOpen={setDeleteOverlayOpen}
                    onConfirm={confirmDeleteRole}
                />
            )}
        </div>
    );
};

export default PropertyDetailsPanel;
